import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link as MuiLink, Tooltip } from '@material-ui/core';
import { AssignmentInd, CheckCircleOutline, ExitToApp, Undo } from '@material-ui/icons';
import { Query } from '@material-table/core';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';
import { IGridQueryRequest, OrderDirection } from '../../../../../Api/AptorApi';
import { useAptorApi } from '../../../../../Api';
import { Container, MTable } from '../../../../../Components';
import { AccessLevel, AdminFeature, UserContext } from '../../../../../Context/UserContext/UserContext';
import { ImpersonationListFilter } from './ImpersonationListFilter';
import { useStyles } from './ImpersonationsTable.styles';
import Select from '../../../../../Components/Select/Select';
import { SelectOption } from '../../../../../Components/Form/Form.types';
import { useApiValidationSnackbar } from '../../../../../Api/useValidationSnackbar';

export type ImpersonationState = 'all' | 'active' | 'expired' | 'pending';

const reasonPreviewLength = 100;

type ImpersonationRow = {
  id: string;
  userEmail: string;
  userName: string;
  reason: string;
  status: 'pending' | 'accepted' | 'active' | 'returned' | 'rejected' | 'expired' | 'completed';
  requestedAtUTC: Date;
  respondBeforeUTC: Date;
  accessValidToUTC?: Date;
  requestedAccessValidToUTC?: Date;
  assignedToId: number;
  assignedToName: string;
  assignedToEmail: string;
  requestedById: number;
  requestedByName: string;
};

export const ImpersonationsTable = () => {
  const classes = useStyles();
  const { api } = useAptorApi();
  const { formatMessage } = useIntl();
  const { notifyUnhandledException } = useApiValidationSnackbar();
  const { impersonateUser, hasAccessToFeature, id: currentUserId, email: currentUserEmail } = useContext(UserContext);
  const navigate = useNavigate();
  const tableRef = React.useRef<any>(null);
  const [status, setStatus] = useState<ImpersonationState>('all');
  const [assignees, setAssignees] = useState<SelectOption[]>([]);
  const [reassignRequest, setReassignRequest] = useState<ImpersonationRow | null>(null);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<number>();
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [completingRequestId, setCompletingRequestId] = useState<string | null>(null);
  const [returningRequestId, setReturningRequestId] = useState<string | null>(null);
  const canManage = hasAccessToFeature(AdminFeature.Administrate, AccessLevel.Manage);

  const openReassignment = async (row: ImpersonationRow) => {
    setReassignRequest(row);
    setSelectedAssigneeId(undefined);
    setAssignees([]);
    setLoadingAssignees(true);
    try {
      const result = await api.getImpersonationUsers(true);
      setAssignees((result.users || [])
        .filter((user) => user.id !== row.assignedToId &&
          user.id !== currentUserId &&
          String(user.value).trim().toLowerCase() !== currentUserEmail.trim().toLowerCase() &&
          String(user.value).toLowerCase() !== row.userEmail.trim().toLowerCase())
        .map((user) => ({ value: user.id, label: user.label })));
    } catch {
      notifyUnhandledException();
    } finally {
      setLoadingAssignees(false);
    }
  };

  const submitReassignment = async () => {
    if (!reassignRequest || !selectedAssigneeId || reassigning) return;
    setReassigning(true);
    try {
      await api.reassignImpersonationRequest(reassignRequest.id, selectedAssigneeId);
      setReassignRequest(null);
      tableRef.current?.onQueryChange();
    } catch {
      notifyUnhandledException();
    } finally {
      setReassigning(false);
    }
  };

  const assignAccessToRequester = async (row: ImpersonationRow) => {
    if (returningRequestId) return;
    setReturningRequestId(row.id);
    try {
      await api.reassignImpersonationRequest(row.id, row.requestedById);
      tableRef.current?.onQueryChange();
    } catch {
      notifyUnhandledException();
    } finally {
      setReturningRequestId(null);
    }
  };

  const fetchRows = useCallback(async (query: Query<ImpersonationRow>) => api.searchImpersonationRequests<ImpersonationRow>({
    orderDirection: query.orderDirection === 'asc' ? OrderDirection.Ascending : OrderDirection.Descending,
    orderBy: query.orderBy ? query.orderBy.field : 'requestedAtUTC',
    page: query.page,
    pageSize: query.pageSize,
    search: query.search,
    filters: { state: [status] },
  } as IGridQueryRequest), [api, status]);

  useEffect(() => {
    tableRef.current?.onQueryChange();
  }, [status]);

  const isAccessible = (row: ImpersonationRow) => ['active', 'accepted', 'returned'].includes(row.status);

  return (
    <Container fullWidth>
      <Box className={classes.toolbar}>
        <ImpersonationListFilter value={status} onChange={setStatus} />
        {canManage && (
          <Button component={Link} to="/admin/law-portal/support-invites/new" variant="outlined">
            {formatMessage({ id: 'admin.impersonation-request.add' })}
          </Button>
        )}
      </Box>
      <MTable
        tableRef={tableRef}
        columns={[
          {
            title: formatMessage({ id: 'admin.impersonation-request.impersonation-user' }),
            field: 'userName',
            render: (row: ImpersonationRow) => <Box className={classes.cellText}>{row.userName} &lt;{row.userEmail}&gt;</Box>,
          },
          {
            title: formatMessage({ id: 'dashboard.impersonation.reason' }),
            field: 'reason',
            sorting: false,
            render: (row: ImpersonationRow) => {
              const reason = row.reason ?? '';
              const isLongReason = reason.length > reasonPreviewLength;
              return (
                <Box className={classes.reasonText}>
                  <span className={classes.reasonPreview}>{reason}</span>
                  {isLongReason && (
                    <Tooltip
                      arrow
                      placement="top"
                      title={reason}
                      classes={{ tooltip: classes.reasonTooltip }}
                    >
                      <MuiLink
                        className={classes.showMoreLink}
                        component="span"
                        tabIndex={0}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {formatMessage({ id: 'utils.show-more' })}
                      </MuiLink>
                    </Tooltip>
                  )}
                </Box>
              );
            },
          },
          {
            title: formatMessage({ id: 'utils.filters.status' }),
            field: 'status',
            render: (row: ImpersonationRow) => row.status === 'returned'
              ? formatMessage({ id: 'admin.impersonation-request.status.returned' })
              : row.status,
          },
          {
            title: formatMessage({ id: 'admin.impersonation-request.assigned-to' }),
            field: 'assignedToName',
            sorting: false,
            render: (row: ImpersonationRow) => <Box className={classes.cellText}>{row.assignedToName} &lt;{row.assignedToEmail}&gt;</Box>,
          },
          { title: formatMessage({ id: 'admin.impersonations.table.requested' }), field: 'requestedAtUTC', defaultSort: 'desc', render: (row: ImpersonationRow) => moment.utc(row.requestedAtUTC).local().format('lll') },
          {
            title: formatMessage({ id: 'admin.impersonations.table.validTo' }),
            field: 'accessValidToUTC',
            render: (row: ImpersonationRow) => {
              const validTo = row.accessValidToUTC || row.requestedAccessValidToUTC;
              return validTo ? moment.utc(validTo).local().format('lll') : '—';
            },
          },
          {
            title: formatMessage({ id: 'admin.impersonation-request.action' }),
            field: 'actions',
            align: 'right',
            sorting: false,
            render: (row: ImpersonationRow) => {
              const isRequestOwner = row.requestedById === currentUserId;
              const isCurrentAssignee = row.assignedToId === currentUserId;
              if (!isAccessible(row) || (!isRequestOwner && !isCurrentAssignee)) return null;
              const accessValidTo = row.accessValidToUTC || row.requestedAccessValidToUTC;
              const hasValidAccess = !!accessValidTo && moment.utc(accessValidTo).valueOf() > Date.now();
              const canReassignRequest = isRequestOwner && isCurrentAssignee && hasValidAccess;
              const canReturnAccess = isCurrentAssignee && !isRequestOwner && hasValidAccess;
              const canReclaimAccess = isRequestOwner && !isCurrentAssignee && hasValidAccess;

              return (
                <Box className={classes.rowActions}>
                  {isCurrentAssignee && (
                    <>
                      <Tooltip title={formatMessage({ id: 'admin.support.table.impersonate' })}>
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label={formatMessage({ id: 'admin.support.table.impersonate' })}
                          onClick={(event) => {
                            event.stopPropagation();
                            impersonateUser(row.userEmail);
                            navigate('/');
                          }}
                        >
                          <ExitToApp />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={formatMessage({ id: 'dashboard.impersonation.complete' })}>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={completingRequestId === row.id}
                          aria-label={formatMessage({ id: 'dashboard.impersonation.complete' })}
                          onClick={async (event) => {
                            event.stopPropagation();
                            if (completingRequestId) return;
                            setCompletingRequestId(row.id);
                            try {
                              await api.completeImpersonationRequest(row.id);
                              tableRef.current?.onQueryChange();
                            } catch {
                              notifyUnhandledException();
                            } finally {
                              setCompletingRequestId(null);
                            }
                          }}
                        >
                          <CheckCircleOutline />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {canReassignRequest && (
                    <Tooltip title={formatMessage({ id: 'admin.impersonation-request.reassign' })}>
                      <IconButton
                        size="small"
                        color="primary"
                        aria-label={formatMessage({ id: 'admin.impersonation-request.reassign' })}
                        onClick={(event) => {
                          event.stopPropagation();
                          openReassignment(row);
                        }}
                      >
                        <AssignmentInd />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canReturnAccess && (
                    <Tooltip title={formatMessage({ id: 'admin.impersonation-request.return' }, { name: row.requestedByName })}>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={returningRequestId === row.id}
                        aria-label={formatMessage({ id: 'admin.impersonation-request.return' }, { name: row.requestedByName })}
                        onClick={(event) => {
                          event.stopPropagation();
                          assignAccessToRequester(row);
                        }}
                      >
                        <Undo />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canReclaimAccess && (
                    <Tooltip title={formatMessage({ id: 'admin.impersonation-request.reclaim' })}>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={returningRequestId === row.id}
                        aria-label={formatMessage({ id: 'admin.impersonation-request.reclaim' })}
                        onClick={(event) => {
                          event.stopPropagation();
                          assignAccessToRequester(row);
                        }}
                      >
                        <Undo />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            },
          },
        ]}
        data={fetchRows}
      />
      <Dialog
        open={Boolean(reassignRequest)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: classes.reassignDialog }}
        onClose={() => !reassigning && setReassignRequest(null)}
      >
        <DialogTitle className={classes.reassignTitle}>
          {formatMessage({ id: 'admin.support.table.user' })}
        </DialogTitle>
        <DialogContent className={classes.reassignContent}>
          <Select
            className={classes.reassignSelect}
            selectProps={{
              options: assignees,
              value: assignees.find((assignee) => Number(assignee.value) === selectedAssigneeId) || null,
              placeholder: formatMessage({ id: 'admin.impersonation-request.reassign.to' }),
              isLoading: loadingAssignees,
              isClearable: false,
              isSearchable: true,
              menuPortalTarget: document.body,
              menuPosition: 'fixed',
              styles: {
                menu: (provided: any) => ({ ...provided, zIndex: 1400 }),
                menuPortal: (provided: any) => ({ ...provided, zIndex: 1400 }),
              },
              onChange: (option) => setSelectedAssigneeId(option ? Number((option as SelectOption).value) : undefined),
            }}
          />
        </DialogContent>
        <DialogActions className={classes.reassignActions}>
          <Button onClick={() => setReassignRequest(null)} disabled={reassigning}>
            {formatMessage({ id: 'form.cancel' })}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!selectedAssigneeId || reassigning || selectedAssigneeId === reassignRequest?.assignedToId}
            onClick={submitReassignment}
          >
            {formatMessage({ id: 'admin.impersonation-request.reassign' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
